import React from 'react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

export default function FAQs() {
  const faqs = [
    {
      question: 'What is the typical project timeline?',
      answer: 'Most residential projects are completed within 8-16 weeks, depending on the scope and complexity. Commercial projects may take 12-24 weeks. We provide a detailed timeline during the initial consultation based on your specific requirements.'
    },
    {
      question: 'Do you work outside Bengaluru and Mumbai?',
      answer: 'Yes, we undertake select projects across India. For projects outside our primary locations, travel and logistics costs apply. We evaluate each opportunity based on project scope and feasibility.'
    },
    {
      question: 'How do consultations work?',
      answer: 'We offer a free initial consultation where we discuss your vision, requirements, budget, and timeline. You can share floor plans, inspiration images, and any specific preferences. Based on this, we present a customized proposal with design concepts and pricing.'
    },
    {
      question: 'What services are included in your design packages?',
      answer: 'Our comprehensive packages include space planning, 3D visualizations, material selection, custom furniture design, lighting design, color consultation, project management, and installation supervision. We can also customize packages based on your needs.'
    },
    {
      question: 'Do you provide 3D visualizations?',
      answer: 'Yes, we create detailed 3D renderings and walkthroughs so you can visualize your space before execution begins. This helps in making informed decisions about layout, colors, and finishes.'
    },
    {
      question: 'What is your payment structure?',
      answer: 'Typically, we follow a milestone-based payment structure: 30% advance upon signing the agreement, 40% upon approval of designs and commencement of work, and 30% upon project completion. We also offer flexible payment plans for larger projects.'
    },
    {
      question: 'Can I make changes during the project?',
      answer: 'Minor adjustments can usually be accommodated. However, significant changes may affect the timeline and budget. We encourage detailed planning during the design phase to minimize mid-project alterations.'
    },
    {
      question: 'Do you handle permits and approvals?',
      answer: 'For projects requiring municipal approvals or permits, we can guide you through the process and coordinate with relevant authorities. This service can be included in turnkey packages.'
    },
    {
      question: 'What materials and finishes do you work with?',
      answer: 'We work with a wide range of premium materials including natural wood, stone, marble, glass, metals, and sustainable options. Our design approach emphasizes quality and durability while staying true to the minimalist aesthetic.'
    },
    {
      question: 'Do you offer warranty or after-sales service?',
      answer: 'Yes, we provide a warranty on workmanship and materials as specified in the contract. We also offer after-sales support and maintenance guidance to ensure your space remains beautiful for years to come.'
    },
    {
      question: 'How is pricing determined?',
      answer: 'Pricing depends on several factors: project size (square footage), complexity, level of customization, materials and finishes selected, and furniture requirements. We provide transparent, detailed quotes with no hidden costs.'
    },
    {
      question: 'Can you work within my budget?',
      answer: 'Absolutely. We work with various budgets and can suggest alternatives for materials and finishes that align with your financial plan without compromising on design quality. Transparency and clear communication about costs are our priorities.'
    }
  ];

  return (
    <div className="py-16 md:py-24" data-testid="faqs-page">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-base md:text-lg font-semibold tracking-wide uppercase text-muted-foreground mb-4">
            Questions & Answers
          </h2>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Find answers to common questions about our services, process, and policies. Can't find what you're looking for? Feel free to contact us.
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-4xl mx-auto mb-16">
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger 
                  className="text-left text-base md:text-lg"
                  data-testid={`faq-question-${index}`}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent 
                  className="text-sm md:text-base text-muted-foreground leading-relaxed"
                  data-testid={`faq-answer-${index}`}
                >
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Card className="p-8 md:p-12 bg-accent max-w-3xl mx-auto">
            <h3 className="text-2xl md:text-3xl font-semibold mb-4">Still Have Questions?</h3>
            <p className="text-muted-foreground mb-8">
              Our team is here to help. Reach out for a personalized consultation or any clarifications you need.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="transition-colors duration-200" data-testid="faqs-contact-button">
                  Contact Us
                </Button>
              </Link>
              <a href="https://wa.me/918310951921" target="_blank" rel="noreferrer">
                <Button variant="secondary" size="lg" className="transition-colors duration-200" data-testid="faqs-whatsapp-button">
                  Chat on WhatsApp
                </Button>
              </a>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}